import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from "react"
import { useParams, Link } from "react-router-dom"
import { useQueryClient } from "react-query"
import { ActionCableConsumer } from "react-actioncable-provider"
import { UserContext } from "../../components/HomeApp"
import { react_post_path } from "../../routes"
import QRCode from "react-qr-code"
import { usePost } from "../../hooks/usePost"
import { useTicketPurchasesForPost } from "../../hooks/useTicketPurchases"
import { usePostPayout } from "../../hooks/usePostPayout"
import { usePayout } from "../../hooks/usePayout"
import {
  useRedeemTicketPurchase,
  useUnredeemTicketPurchase,
  useDeleteTicketPurchase,
  useUpdateTicketPurchase,
} from "../../hooks/useTicketPurchaseMutations"
import type { TicketPurchaseWithPurchaser } from "../../requests/ticketPurchases"
import { compTicket } from "../../requests/ticketPurchases"
import { updateEvent } from "../../util/backend_api/posts"
import {
  generateTicketsCSV,
  downloadCSV,
  type PayoutCSVInfo,
} from "./csvExport"
import { TicketsViewDesktop } from "./TicketsViewDesktop"
import { TicketsViewMobile } from "./TicketsViewMobile"
import { EventInformation, type EventInformationPost } from "./EventInformation"
import { Stats } from "./Stats"
import { CashTickets } from "./CashTickets"
import { AddCashTicketsModal } from "./AddCashTicketsModal"
import { AddGuestModal } from "./AddGuestModal"
import { Spinner } from "@components/shared/Spinner"
import { RequestError } from "@components/shared/RequestError"

/** Post shape from usePost when requesting content_hash,event_ticket_summary */
interface PostInfo extends EventInformationPost {
  band_id?: number
  content_hash?: Record<string, unknown>
}

const PER_PAGE = 200

const TicketManagementPage: React.FC = () => {
  const { user } = useContext(UserContext)
  const { postId } = useParams<{ postId: string }>()
  const postIdNumber = postId ? parseInt(postId, 10) : null
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [filterRedeemed, setFilterRedeemed] = useState<
    "all" | "redeemed" | "unredeemed"
  >("all")
  const [page, setPage] = useState(1)
  const [expandedTickets, setExpandedTickets] = useState<Set<number>>(new Set())
  const [showAddCashModal, setShowAddCashModal] = useState(false)
  const [addCashSubmitting, setAddCashSubmitting] = useState(false)
  const [resettingCashTickets, setResettingCashTickets] = useState(false)
  const [showAddGuestModal, setShowAddGuestModal] = useState(false)
  const [addGuestSubmitting, setAddGuestSubmitting] = useState(false)

  // Debounce search term so refetch doesn't run on every keystroke (avoids scroll jump / disruptive re-renders)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchTerm])

  // Prepare params for the hook
  const redeemedFilter = useMemo(() => {
    if (filterRedeemed === "all") return undefined
    return filterRedeemed === "redeemed"
  }, [filterRedeemed])

  const queryClient = useQueryClient()
  // Load post with content_hash for Add Cash Tickets default price and submit merge
  const {
    data: postData,
    isLoading: postLoading,
    error: postError,
  } = usePost(postId ?? undefined, "content_hash,event_ticket_summary")
  const post = (postData as { post?: PostInfo } | undefined)?.post
  const defaultPricePerTicket = useMemo(() => {
    const ch = post?.content_hash
    if (!ch) return 0
    const door = ch["ticket_price_at_door"]
    const nada = ch["ticket_price"]
    const n = typeof door === "number" ? door : parseFloat(String(door || 0))
    const b = typeof nada === "number" ? nada : parseFloat(String(nada || 0))
    return n || b || 0
  }, [post])

  // When all tickets fit in one page, filter in JS (instant, no round-trip).
  // Only fall back to server-side search when the event has more than PER_PAGE tickets.
  // allLoaded is stored in state so serverParams can be computed before the hook call.
  const [allLoaded, setAllLoaded] = useState<boolean | null>(null)

  const serverParams = useMemo(() => {
    if (allLoaded === null || allLoaded) {
      return { per_page: PER_PAGE }
    }
    return {
      per_page: PER_PAGE,
      search: debouncedSearchTerm || undefined,
      redeemed: redeemedFilter,
      page,
    }
  }, [allLoaded, debouncedSearchTerm, redeemedFilter, page])

  const { data } = useTicketPurchasesForPost(postIdNumber, serverParams, {
    enabled: postIdNumber !== null,
  })

  useEffect(() => {
    if (data?.has_next_page !== undefined && allLoaded === null) {
      setAllLoaded(!data.has_next_page)
    }
  }, [data?.has_next_page, allLoaded])

  const allTickets = useMemo(() => data?.tickets ?? [], [data?.tickets])

  // Include payout info in the CSV export if a payout exists for this post yet, whatever
  // state it's currently in (not yet released, released, partially distributed, or sent).
  const { data: postPayoutData } = usePostPayout(postIdNumber)
  const payoutId = postPayoutData?.payout_id ?? null
  const { data: payoutDetailData } = usePayout(payoutId ?? "", {
    enabled: !!payoutId,
  })
  const payout = (payoutDetailData as { payout?: PayoutCSVInfo } | undefined)
    ?.payout

  // Mutation hooks
  const redeemMutation = useRedeemTicketPurchase()
  const unredeemMutation = useUnredeemTicketPurchase()
  const deleteMutation = useDeleteTicketPurchase()
  const cancelRefundMutation = useUpdateTicketPurchase()

  // Extract data from tickets response
  const stats = useMemo(
    () => ({
      total: data?.total_count || 0,
      redeemed: data?.redeemed_count || 0,
      unredeemed: data?.unredeemed_count || 0,
      comped: data?.comped_count || 0,
    }),
    [data],
  )

  const tickets = useMemo(() => {
    if (!allLoaded) return allTickets // server already filtered

    const q = searchTerm.toLowerCase()
    return allTickets.filter((t) => {
      if (filterRedeemed === "redeemed" && !t.redeemed) return false
      if (filterRedeemed === "unredeemed" && t.redeemed) return false
      if (!q) return true
      return (
        t.purchaser?.first_name?.toLowerCase().includes(q) ||
        t.purchaser?.last_name?.toLowerCase().includes(q) ||
        t.purchaser?.email?.toLowerCase().includes(q) ||
        t.ticket_id?.toLowerCase().includes(q) ||
        t.guest_name?.toLowerCase().includes(q) ||
        t.guest_email?.toLowerCase().includes(q)
      )
    })
  }, [allLoaded, allTickets, searchTerm, filterRedeemed])

  const handleRedeem = async (ticketId: number) => {
    if (!postIdNumber) return
    try {
      await redeemMutation.mutateAsync({
        postId: postIdNumber,
        ticketPurchaseId: ticketId,
      })
    } catch (error) {
      console.error("Error redeeming ticket:", error)
    }
  }

  const handleUnredeem = async (ticketId: number) => {
    if (!postIdNumber) return
    try {
      await unredeemMutation.mutateAsync({
        postId: postIdNumber,
        ticketPurchaseId: ticketId,
      })
    } catch (error) {
      console.error("Error unredeeming ticket:", error)
    }
  }

  const handleDelete = async (ticketId: number) => {
    if (!postIdNumber) return
    if (!window.confirm("Remove this person from the guest list?")) return
    try {
      await deleteMutation.mutateAsync({
        postId: postIdNumber,
        ticketPurchaseId: ticketId,
      })
    } catch (error) {
      console.error("Error deleting guest list ticket:", error)
    }
  }

  const handleCancelRefund = async (ticket: TicketPurchaseWithPurchaser) => {
    if (
      !window.confirm(
        "Cancel this ticket and refund the purchaser? This cannot be undone.",
      )
    )
      return
    try {
      await cancelRefundMutation.mutateAsync({
        bandId: ticket.band_id,
        ticketPurchaseId: ticket.id,
        params: { ticket_purchase: { status: "cancelled" } },
      })
    } catch (error) {
      console.error("Error cancelling/refunding ticket:", error)
    }
  }

  const toggleExpanded = (ticketId: number) => {
    const newExpanded = new Set(expandedTickets)
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId)
    } else {
      newExpanded.add(ticketId)
    }
    setExpandedTickets(newExpanded)
  }

  const handleAddCashTicketsSubmit = async (
    numTickets: number,
    totalCashAmount: number,
  ) => {
    if (!postIdNumber || !user?.band?.id || post.content_hash === undefined)
      return
    setAddCashSubmitting(true)
    try {
      const existingPurchased = Number(
        post.content_hash?.["cash_tickets_purchased"] ?? 0,
      )
      const existingTotal = Number(
        post.content_hash?.["cash_tickets_total"] ?? 0,
      )
      const newPurchased = existingPurchased + numTickets
      const newTotal = existingTotal + totalCashAmount
      const mergedContentHash = {
        ...post.content_hash,
        cash_tickets_purchased: newPurchased,
        cash_tickets_total: newTotal,
      }
      const result = await updateEvent(
        user.band.id,
        postIdNumber,
        { content_hash: mergedContentHash },
        [],
        false,
      )
      if ((result as { error?: unknown })?.error) {
        console.error("Failed to update post with cash tickets", result)
        setAddCashSubmitting(false)
        return
      }
      await queryClient.invalidateQueries({ queryKey: ["post", postId] })
      setShowAddCashModal(false)
    } finally {
      setAddCashSubmitting(false)
    }
  }

  const handleResetCashTicketCount = async () => {
    if (!postIdNumber || !user?.band?.id || post.content_hash === undefined)
      return
    setResettingCashTickets(true)
    try {
      const mergedContentHash = {
        ...post.content_hash,
        cash_tickets_purchased: 0,
        cash_tickets_total: 0,
      }
      const result = await updateEvent(
        user.band.id,
        postIdNumber,
        { content_hash: mergedContentHash },
        [],
        false,
      )
      if ((result as { error?: unknown })?.error) {
        console.error("Failed to reset cash ticket count", result)
        setResettingCashTickets(false)
        return
      }
      await queryClient.invalidateQueries({ queryKey: ["post", postId] })
    } finally {
      setResettingCashTickets(false)
    }
  }

  const handleAddGuestSubmit = async (
    guestName: string,
    guestEmail?: string,
  ) => {
    if (!postIdNumber) return
    setAddGuestSubmitting(true)
    try {
      const result = await compTicket(postIdNumber, guestName, guestEmail)
      if ((result as { error?: unknown })?.error) {
        console.error("Failed to add guest ticket", result)
        return
      }
      await queryClient.invalidateQueries(["ticketPurchases", postIdNumber])
      setShowAddGuestModal(false)
    } finally {
      setAddGuestSubmitting(false)
    }
  }

  const handleTicketPurchased = useCallback(
    (data: { type: string }) => {
      if (data.type === "ticket_purchased" && postIdNumber !== null) {
        queryClient.invalidateQueries(["ticketPurchases", postIdNumber])
      }
    },
    [queryClient, postIdNumber],
  )

  const handleDownloadCSV = () => {
    if (!tickets || tickets.length === 0 || !post) return

    const csvContent = generateTicketsCSV({ post, tickets, stats, payout })
    const filename = `tickets-${post.title?.replace(/[^a-z0-9]/gi, "-").toLowerCase() || "export"}-${new Date().toISOString().split("T")[0]}.csv`
    downloadCSV(csvContent, filename)
  }

  return (
    <div className="lg:max-w-6xl max-w-full mx-auto p-4 lg:p-6">
      {postIdNumber !== null && (
        <ActionCableConsumer
          channel={{ channel: "PostChannel", id: postIdNumber }}
          onReceived={handleTicketPurchased}
        />
      )}
      {/* Back Link */}
      <div className="mb-4">
        <Link
          to={react_post_path(postId!)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Event
        </Link>
      </div>

      {postLoading ? (
        <Spinner />
      ) : postError ? (
        <RequestError error={postError} />
      ) : (
        <>
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl lg:text-3xl font-black uppercase text-black-800 leading-none [overflow-wrap:anywhere]">
                  {post.title}
                </h1>
                <h2 className="text-gray-600 mb-2">Manage Tickets</h2>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadCSV}
                    disabled={!tickets || tickets.length === 0}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Download CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddGuestModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none"
                  >
                    + Guest List
                  </button>
                </div>
              </div>
              <div className="mb-4 shrink-0">
                <QRCode value={post.nada_url} size={160} level="M" />
              </div>
            </div>

            <EventInformation
              post={post}
              cashTicketsTotal={
                post.content_hash?.["cash_tickets_total"] as
                  | number
                  | null
                  | undefined
              }
              cashTicketsPurchased={
                post.content_hash?.["cash_tickets_purchased"] as
                  | number
                  | null
                  | undefined
              }
            />
            <Stats
              total={stats?.total}
              redeemed={stats?.redeemed}
              unredeemed={stats?.unredeemed}
              comped={stats?.comped}
            />
            <CashTickets
              cashTicketsPurchased={
                post.content_hash?.["cash_tickets_purchased"] as
                  | number
                  | null
                  | undefined
              }
              cashTicketsTotal={
                post.content_hash?.["cash_tickets_total"] as
                  | number
                  | null
                  | undefined
              }
              onAddCashTicket={() => setShowAddCashModal(true)}
              onResetCashTicketCount={handleResetCashTicketCount}
              isResetting={resettingCashTickets}
            />
          </div>
        </>
      )}

      {/* Ticket Section */}

      <div className="min-h-screen">
        {/* Filters */}
        <div className="mb-6 flex flex-col lg:flex-row gap-4 sticky top-0 bg-white py-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, or ticket ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur()
              }}
              autoComplete="new-password"
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm"
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none"
            >
              Clear
            </button>
          )}
          <select
            value={filterRedeemed}
            onChange={(e) => {
              setFilterRedeemed(
                e.target.value as "all" | "redeemed" | "unredeemed",
              )
              setPage(1)
            }}
            className="px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm bg-white"
          >
            <option value="all">All Tickets</option>
            <option value="unredeemed">Unredeemed Only</option>
            <option value="redeemed">Redeemed Only</option>
          </select>
        </div>

        <TicketsViewDesktop
          tickets={tickets as TicketPurchaseWithPurchaser[]}
          onRedeem={handleRedeem}
          onUnredeem={handleUnredeem}
          onDelete={handleDelete}
          onCancelRefund={handleCancelRefund}
          isRedeemLoading={redeemMutation.isLoading}
          isUnredeemLoading={unredeemMutation.isLoading}
          isDeleteLoading={deleteMutation.isLoading}
          isCancelRefundLoading={cancelRefundMutation.isLoading}
        />

        <TicketsViewMobile
          tickets={tickets as TicketPurchaseWithPurchaser[]}
          expandedTickets={expandedTickets}
          onToggleExpanded={toggleExpanded}
          onRedeem={handleRedeem}
          onUnredeem={handleUnredeem}
          onDelete={handleDelete}
          onCancelRefund={handleCancelRefund}
          isRedeemLoading={redeemMutation.isLoading}
          isUnredeemLoading={unredeemMutation.isLoading}
          isDeleteLoading={deleteMutation.isLoading}
          isCancelRefundLoading={cancelRefundMutation.isLoading}
        />

        {tickets.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-sm">
              No tickets found matching your criteria.
            </div>
          </div>
        )}

        {!allLoaded && (data?.total_pages ?? 1) > 1 && (
          <div className="flex items-center justify-between mt-6">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {data?.page ?? page} of {data?.total_pages ?? 1}
            </span>
            <button
              type="button"
              onClick={() =>
                setPage((p) => Math.min(data?.total_pages ?? p, p + 1))
              }
              disabled={page >= (data?.total_pages ?? 1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AddCashTicketsModal
        open={showAddCashModal}
        onClose={() => setShowAddCashModal(false)}
        defaultPricePerTicket={defaultPricePerTicket}
        onSubmit={handleAddCashTicketsSubmit}
        isSubmitting={addCashSubmitting}
        ticketsRemaining={
          post?.event_ticket_summary?.tickets_remaining ??
          post?.tickets_remaining
        }
        maxTickets={
          post?.event_ticket_summary?.max_tickets ?? post?.max_tickets
        }
        cashTicketsPurchased={
          post?.content_hash?.["cash_tickets_purchased"] as
            | number
            | null
            | undefined
        }
      />
      <AddGuestModal
        open={showAddGuestModal}
        onClose={() => setShowAddGuestModal(false)}
        onSubmit={handleAddGuestSubmit}
        isSubmitting={addGuestSubmitting}
      />
    </div>
  )
}

export default TicketManagementPage
